import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  CanActivate,
  UrlTree,
  Router,
  ActivatedRoute,
} from '@angular/router';
import { isPlatformServer } from '@angular/common';

import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { take, map } from 'rxjs/operators';

import { snapshot } from '@fcom/rx';
import { AppState, SentryLogger } from '@fcom/core';
import { DeepLinkSource } from '@fcom/common/interfaces';
import { hasOrderData, OrderState, orderState } from '@fcom/common-booking';

import { AuthActions, DataActions } from '../store/actions';
import { getError } from '../store';
import { UpgradeError } from '../interfaces';

@Injectable()
export class LoginGuard implements CanActivate {
  constructor(
    private store: Store<AppState>,
    private router: Router,
    private sentryLogger: SentryLogger,
    private activatedRoute: ActivatedRoute,
    @Inject(PLATFORM_ID) private platform: object
  ) {}

  setSource(route: ActivatedRouteSnapshot): void {
    const mappedSource = DeepLinkSource[route.queryParamMap.get('source')];

    if (mappedSource) {
      this.store.dispatch(
        DataActions.setDeepLinkSource({
          source: DeepLinkSource[route.queryParamMap.get('source')],
        })
      );
    } else {
      this.sentryLogger.warn(`Unexpected source in deeplink url: '${route.queryParamMap.get('source')}'. Skipping`);
    }
  }

  canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    if (isPlatformServer(this.platform)) {
      // Always return true on server so that client handles the requests
      return of(true);
    }

    const error = snapshot(this.store.pipe(select(getError)));

    if (error !== UpgradeError.httpError) {
      this.store.dispatch(AuthActions.logout());
    }

    if (route.queryParamMap.has('source')) {
      this.setSource(route);
    }

    // Handle deeplink with query parameters in the url.
    if (route.queryParamMap.has('recLoc') && route.queryParamMap.has('lastName')) {
      this.store.dispatch(
        AuthActions.setBookingRef({
          recLoc: route.queryParamMap.get('recLoc'),
          lastName: route.queryParamMap.get('lastName'),
        })
      );

      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {},
        queryParamsHandling: 'merge',
      });

      this.store.dispatch(AuthActions.loadPNR());

      return of(true);
    }

    // Handle deeplink from MMB with booking data in the store.
    return combineLatest([this.store.pipe(hasOrderData()), this.store.pipe(orderState())]).pipe(
      take(1),
      map(([hasOrder, order]: [boolean, OrderState]) => {
        if (hasOrder) {
          this.store.dispatch(
            AuthActions.setBookingRef({
              recLoc: order.orderData.id,
              lastName: order.orderData.passengers[0].lastName,
            })
          );

          this.store.dispatch(
            DataActions.setDeepLinkSource({
              source: DeepLinkSource.FCOM,
            })
          );
          this.store.dispatch(AuthActions.loadPNR());
        }

        return true;
      })
    );
  }
}
